import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { describeAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logError } from "@/lib/logger";
import {
    DownloadSimple,
    ShieldCheck,
    Trash,
    Warning,
    Clock,
    CheckCircle,
    Lock,
    FileText,
} from "@phosphor-icons/react";

/**
 * Candidate self-service page for DPDPA / GDPR data rights:
 *   1) Download everything we hold about you as JSON (portability)
 *   2) View your consent history (transparency)
 *   3) Permanently delete your account (erasure)
 *
 * Route: /candidate/my-data (CandidateOnly guard in App.js).
 */
export default function MyData() {
    const { user, setUser } = useAuth();
    const nav = useNavigate();

    // ---------------------------------------------------------------------
    // Consent history
    // ---------------------------------------------------------------------
    const [consents, setConsents] = useState([]);
    const [loadingConsents, setLoadingConsents] = useState(true);
    useEffect(() => {
        api
            .get("/me/consents")
            .then((r) => setConsents(r.data.items || []))
            .catch((e) => logError("Consent history load failed:", e))
            .finally(() => setLoadingConsents(false));
    }, []);

    // ---------------------------------------------------------------------
    // Data export
    // ---------------------------------------------------------------------
    const [exporting, setExporting] = useState(false);
    const [lastExport, setLastExport] = useState(null);

    const downloadExport = async () => {
        setExporting(true);
        try {
            const { data } = await api.get("/me/data-export");
            const blob = new Blob([JSON.stringify(data, null, 2)], {
                type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `naukridaddy-my-data-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            setLastExport(data);
            toast.success("Your data was downloaded.");
        } catch (e) {
            toast.error(describeAxiosError(e));
        } finally {
            setExporting(false);
        }
    };

    // ---------------------------------------------------------------------
    // Delete account
    // ---------------------------------------------------------------------
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmEmail, setConfirmEmail] = useState("");
    const [reason, setReason] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [delError, setDelError] = useState("");

    const submitDelete = async (e) => {
        e.preventDefault();
        setDelError("");
        if (confirmEmail.trim().toLowerCase() !== (user?.email || "").toLowerCase()) {
            setDelError("The email doesn't match your account email.");
            return;
        }
        setDeleting(true);
        try {
            await api.delete("/me/account", {
                data: { confirm_email: confirmEmail.trim(), reason },
            });
            toast.success("Your account has been deleted.");
            // Order matters: nav BEFORE setUser(false) — otherwise the
            // CandidateOnly guard on this route will intercept the state
            // change and bounce to /candidate/login instead of "/".
            nav("/", { replace: true });
            setUser(false);
        } catch (ex) {
            setDelError(describeAxiosError(ex));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div data-testid="my-data-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8">
            <div>
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt inline-flex items-center gap-2 mb-2">
                    <ShieldCheck size={12} weight="fill" /> Privacy & Data
                </p>
                <h1 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight">
                    Your data, your controls.
                </h1>
                <p className="text-sm text-[#525252] mt-3 max-w-2xl">
                    We follow India&apos;s DPDPA 2023 and GDPR — you own your data.
                    Download it, review what you&apos;ve consented to, and delete
                    your account at any time.
                </p>
            </div>

            {/* Export */}
            <section
                data-testid="my-data-export-section"
                className="border border-[#E5E5E5] bg-white p-5 sm:p-6"
            >
                <div className="flex items-start gap-4">
                    <div className="p-2 bg-cobalt/10 text-cobalt shrink-0">
                        <DownloadSimple size={20} weight="bold" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 className="font-heading text-lg font-bold tracking-tight">
                            Download my data
                        </h2>
                        <p className="text-sm text-[#525252] mt-1">
                            One JSON file with everything we hold about you: profile,
                            applications, consent history, referrals, testimonials,
                            saved searches, and boost purchases.
                        </p>
                        <button
                            data-testid="my-data-export-button"
                            type="button"
                            onClick={downloadExport}
                            disabled={exporting}
                            className="btn-gradient mt-4 px-5 py-2.5 text-xs font-bold tracking-wide inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            {exporting ? "Preparing…" : "Download JSON"}{" "}
                            <DownloadSimple size={14} weight="bold" />
                        </button>
                        {lastExport && (
                            <p
                                data-testid="my-data-export-summary"
                                className="mt-3 text-xs text-[#525252] flex flex-wrap gap-3"
                            >
                                <span>
                                    Applications: <strong>{lastExport.counts?.applications ?? 0}</strong>
                                </span>
                                <span>
                                    Consents: <strong>{lastExport.counts?.consents ?? 0}</strong>
                                </span>
                                <span>
                                    Referrals: <strong>{lastExport.counts?.referrals_given ?? 0}</strong>
                                </span>
                                <span>
                                    Boost purchases: <strong>{lastExport.counts?.boost_purchases ?? 0}</strong>
                                </span>
                            </p>
                        )}
                    </div>
                </div>
            </section>

            {/* Consent history */}
            <section
                data-testid="my-data-consents-section"
                className="border border-[#E5E5E5] bg-white p-5 sm:p-6"
            >
                <div className="flex items-start gap-4">
                    <div className="p-2 bg-emerald-100 text-emerald-700 shrink-0">
                        <FileText size={20} weight="bold" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 className="font-heading text-lg font-bold tracking-tight">
                            Consent history
                        </h2>
                        <p className="text-sm text-[#525252] mt-1">
                            Every time you accepted our Terms or Privacy Policy — with the
                            IP, timestamp, and versioned document hashes we stored.
                        </p>

                        {loadingConsents ? (
                            <p className="text-xs text-[#A3A3A3] mt-4">Loading…</p>
                        ) : consents.length === 0 ? (
                            <p
                                data-testid="my-data-consents-empty"
                                className="text-xs text-[#A3A3A3] mt-4"
                            >
                                No consent records for your account. If you signed up
                                before we launched consent tracking, no worries — your
                                account is still governed by our current Terms.
                            </p>
                        ) : (
                            <ul data-testid="my-data-consents-list" className="mt-4 space-y-2">
                                {consents.map((c, idx) => (
                                    <li
                                        key={c.id || idx}
                                        data-testid={`my-data-consent-${idx}`}
                                        className="border border-[#F5F5F5] bg-[#FAFAFA] p-3 text-xs"
                                    >
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                                            <span className="font-bold text-ink">
                                                {c.context || "candidate_signup"}
                                            </span>
                                            <span className="text-[#A3A3A3]">·</span>
                                            <Clock size={11} className="text-[#525252]" />
                                            <span className="font-mono text-[10px]">
                                                {new Date(c.accepted_at).toLocaleString()}
                                            </span>
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {(c.documents || []).map((d, i) => (
                                                <span
                                                    key={`d-${idx}-${i}`}
                                                    title={d.text_hash}
                                                    className="text-[10px] bg-white border border-[#E5E5E5] px-1.5 py-0.5 font-mono"
                                                >
                                                    {d.kind === "terms_of_service" ? "T&C" : "Privacy"}{" "}
                                                    {d.version}
                                                </span>
                                            ))}
                                        </div>
                                        <p className="text-[10px] text-[#A3A3A3] mt-2 font-mono">
                                            {c.ip} · {(c.user_agent || "").slice(0, 60)}
                                            {c.user_agent && c.user_agent.length > 60 ? "…" : ""}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </section>

            {/* Delete account */}
            <section
                data-testid="my-data-delete-section"
                className="border-2 border-[#DC2626]/40 bg-[#FEF2F2] p-5 sm:p-6"
            >
                <div className="flex items-start gap-4">
                    <div className="p-2 bg-[#DC2626] text-white shrink-0">
                        <Trash size={20} weight="bold" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 className="font-heading text-lg font-bold tracking-tight text-[#7F1D1D]">
                            Delete my account
                        </h2>
                        <p className="text-sm text-[#7F1D1D]/80 mt-1">
                            Permanently erases your profile, applications, testimonials,
                            saved searches, and referrals. Payment records are stripped of
                            personal data but kept 7 years for tax compliance. This action
                            cannot be undone.
                        </p>

                        {!confirmOpen ? (
                            <button
                                data-testid="my-data-delete-open-button"
                                type="button"
                                onClick={() => setConfirmOpen(true)}
                                className="mt-4 px-5 py-2.5 text-xs font-bold tracking-wide inline-flex items-center gap-2 bg-white border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white transition-colors"
                            >
                                <Trash size={14} weight="bold" /> Continue to delete
                            </button>
                        ) : (
                            <form
                                data-testid="my-data-delete-form"
                                onSubmit={submitDelete}
                                className="mt-4 space-y-3 bg-white p-4 border border-[#DC2626]/30"
                            >
                                <p className="text-xs text-[#7F1D1D] flex items-start gap-2">
                                    <Warning size={14} weight="fill" className="text-[#DC2626] shrink-0 mt-0.5" />
                                    Type your account email <span className="font-mono font-bold">{user?.email}</span> to confirm.
                                </p>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-1 block">
                                        Confirm email
                                    </label>
                                    <input
                                        data-testid="my-data-delete-confirm-email"
                                        type="email"
                                        value={confirmEmail}
                                        onChange={(e) => setConfirmEmail(e.target.value)}
                                        required
                                        autoComplete="off"
                                        className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#DC2626]"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-1 block">
                                        Reason (optional, helps us improve)
                                    </label>
                                    <textarea
                                        data-testid="my-data-delete-reason"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        rows={2}
                                        maxLength={500}
                                        className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#DC2626]"
                                    />
                                </div>
                                {delError && (
                                    <div
                                        data-testid="my-data-delete-error"
                                        className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-xs px-3 py-2"
                                    >
                                        {delError}
                                    </div>
                                )}
                                <div className="flex flex-wrap items-center gap-3">
                                    <button
                                        data-testid="my-data-delete-submit"
                                        type="submit"
                                        disabled={deleting}
                                        className="px-5 py-2.5 text-xs font-bold tracking-wide inline-flex items-center gap-2 bg-[#DC2626] text-white hover:bg-[#B91C1C] disabled:opacity-50"
                                    >
                                        {deleting ? "Deleting…" : "Delete my account permanently"}
                                    </button>
                                    <button
                                        data-testid="my-data-delete-cancel"
                                        type="button"
                                        onClick={() => {
                                            setConfirmOpen(false);
                                            setConfirmEmail("");
                                            setReason("");
                                            setDelError("");
                                        }}
                                        className="text-xs font-bold text-[#525252] underline underline-offset-2"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            <p className="text-xs text-[#A3A3A3] flex items-center gap-2">
                <Lock size={12} weight="bold" /> DPDPA · GDPR · we respond to data-rights requests within 30 days.
            </p>
        </div>
    );
}
